import { Icon } from "./Icon";
import { moneyFormatter } from "../lib/money";
import type { OrderSummary } from "../types/catalog";
import "./OrdersPanel.css";

type AdminCartPanelProps = {
  currentAdminEmail: string | null;
  onOpenFulfillment: (orderId: string) => void;
  onRefresh: () => void;
  orders: OrderSummary[];
};

function readableStatus(status: string) {
  return status.replaceAll("_", " ");
}

function orderReference(order: OrderSummary) {
  return order.orderCode || order.id.slice(0, 8);
}

function assignmentLabel(order: OrderSummary, currentAdminEmail: string | null) {
  if (!order.claimedBy) {
    return "Unclaimed";
  }

  return order.claimedBy === currentAdminEmail ? "Claimed by you" : `Claimed by ${order.claimedBy}`;
}

function isCompleted(order: OrderSummary) {
  return order.status === "completed";
}

export function AdminCartPanel({
  currentAdminEmail,
  onOpenFulfillment,
  onRefresh,
  orders,
}: AdminCartPanelProps) {
  const activeOrders = orders.filter((order) => !isCompleted(order));
  const completedOrders = orders.filter((order) => isCompleted(order));

  return (
    <section className="commerce-panel" aria-labelledby="admin-cart-title">
      <div className="orders-toolbar">
        <button
          aria-label="Refresh fulfillment orders"
          className="button button--secondary"
          type="button"
          onClick={onRefresh}
        >
          <Icon name="refresh" />
          Refresh orders
        </button>
      </div>

      <h2 className="sr-only" id="admin-cart-title">Your fulfillment orders</h2>

      {orders.length === 0 ? (
        <article className="order-card order-card--empty">
          <div className="order-card__status-row">
            <p className="eyebrow">Fulfillment</p>
            <span className="role-pill">empty</span>
          </div>
          <h3>No claimed orders yet.</h3>
          <p>Orders you claim will appear here for packing, payment checks, and final status updates.</p>
        </article>
      ) : (
        <div className="order-list">
          {activeOrders.length > 0 ? <p className="eyebrow">Active fulfillment</p> : null}
          {activeOrders.map((order) => (
            <article className="order-card" key={order.id}>
              <div className="order-card__status-row">
                <p className="eyebrow">{readableStatus(order.paymentStatus)}</p>
                <div className="order-card__status-badges">
                  {order.needsAttention ? <span className="role-pill role-pill--warning">Needs attention</span> : null}
                  <span className="role-pill">{readableStatus(order.status)}</span>
                </div>
              </div>
              <div className="order-card__main">
                <h3>{order.customerName}</h3>
                <p>{order.phone}</p>
              </div>
              <dl className="order-card__meta">
                <div>
                  <dt>Total</dt>
                  <dd>{moneyFormatter.format(order.totalKes)}</dd>
                </div>
                <div>
                  <dt>Items</dt>
                  <dd>{order.itemCount}</dd>
                </div>
                <div>
                  <dt>Order</dt>
                  <dd>{orderReference(order)}</dd>
                </div>
                <div>
                  <dt>Assignment</dt>
                  <dd>{assignmentLabel(order, currentAdminEmail)}</dd>
                </div>
              </dl>
              <div className="order-card__actions">
                <button className="button button--secondary" type="button" onClick={() => onOpenFulfillment(order.id)}>
                  Open fulfillment
                </button>
              </div>
            </article>
          ))}
          {completedOrders.length > 0 ? <p className="eyebrow">Completed by you</p> : null}
          {completedOrders.map((order) => (
            <article className="order-card" key={order.id}>
              <div className="order-card__status-row">
                <p className="eyebrow">{readableStatus(order.paymentStatus)}</p>
                <span className="role-pill">{readableStatus(order.status)}</span>
              </div>
              <div className="order-card__main">
                <h3>{order.customerName}</h3>
                <p>{order.phone}</p>
              </div>
              <dl className="order-card__meta">
                <div>
                  <dt>Total</dt>
                  <dd>{moneyFormatter.format(order.totalKes)}</dd>
                </div>
                <div>
                  <dt>Items</dt>
                  <dd>{order.itemCount}</dd>
                </div>
                <div>
                  <dt>Order</dt>
                  <dd>{orderReference(order)}</dd>
                </div>
                <div>
                  <dt>Assignment</dt>
                  <dd>{assignmentLabel(order, currentAdminEmail)}</dd>
                </div>
              </dl>
              <div className="order-card__actions">
                <button className="button button--ghost" type="button" onClick={() => onOpenFulfillment(order.id)}>
                  View details
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
